import { useMachine } from '@xstate/react';
import { Interval } from 'date-fns';
import React, { useEffect } from 'react';
import { DayPicker, Labels } from 'react-day-picker';
import { AnyState } from 'xstate';
import { Day } from './Day';
import { OnChangeProps, PickerProps } from './models';
import { convertToMatcherArray, createDateMachine, createWeekNumberComponent } from './utils';

export interface DatePickerProps extends PickerProps {
  value?: Date;
  onChange?: (props: OnChangeProps) => void;
  labels?: Partial<Labels>;
  children?: (props: {
    send: (type: string, data: object) => void;
    state: AnyState;
    date: Date;
    range: Interval;
  }) => React.JSX.Element;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  locale,
  startOfWeek,
  children,
  onChange = (): void => undefined,
  value: date = new Date(),
  labels,
  ...props
}) => {
  const [state, send, service] = useMachine(() => createDateMachine(date));

  useEffect(() => {
    const subscription = service.subscribe((current) => {
      onChange({ range: current.context.range, date: current.context.range.start, state });
    });

    return subscription.unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, onChange]);

  const { start: current } = state.context.range;

  const dayPickerProps = {
    mode: undefined,
    components: {
      Day,
      WeekNumber: createWeekNumberComponent(convertToMatcherArray(props.disabled)),
    },
    locale,
    onDayClick: (day: Date) => {
      send('click:day', { date: day });
    },
    selected: current,
    weekStartsOn: startOfWeek,
    labels,
    ...props,
  };

  return (
    <section className="date-picker flex flex-col w-min">
      <header />
      <DayPicker {...dayPickerProps} />
      {children && <div className="p-2">{children({ send, state, date: current, range: state.context.range })}</div>}
    </section>
  );
};
